import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Divider, IconButton, Menu, MenuItem, Select, Tooltip } from '@mui/material';
import { useColorScheme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import { useProjectContext } from '../features/planning/context/useProjectContext';
import { useProjects } from '../features/planning/hooks/useProjects';
import { getInitials } from '../utils/user';
import { useCurrentUser } from '../features/auth/hooks/useCurrentUser';
import { useIsSuperAdmin } from '../features/auth/hooks/useIsSuperAdmin';
import { useSignOut } from '../features/auth/hooks/useSignOut';
import { PLANNING_LABELS, SETTINGS_NAV_LABELS as LABELS } from '../utils/labels';

export default function SettingsNav() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [userAnchor, setUserAnchor] = useState<HTMLElement | null>(null);
  const [mobileAnchor, setMobileAnchor] = useState<HTMLElement | null>(null);
  const user = useCurrentUser();
  const initials = user ? getInitials(user) : '';
  const isSuperAdmin = useIsSuperAdmin(user);
  const { signOut } = useSignOut();

  const { mode, setMode } = useColorScheme();

  const { orgId, selectedProjectId, onProjectChange } = useProjectContext();
  const { projects } = useProjects(orgId);
  const activeProjects = projects.filter((p) => !p.archived);
  const archivedProjects = projects.filter((p) => p.archived);
  const validProjectId = projects.some((p) => p.id === selectedProjectId) ? selectedProjectId : null;
  const currentProjectId = validProjectId ?? projects[0]?.id ?? null;

  useEffect(() => {
    if (currentProjectId && currentProjectId !== selectedProjectId) {
      onProjectChange(currentProjectId);
    }
  }, [currentProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mode || mode === 'system') {
      setMode(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    }
  }, []);

  const isDark = mode === 'dark';
  const handleThemeToggle = () => setMode(isDark ? 'light' : 'dark');
  const themeTooltip = isDark ? LABELS.themeLight : LABELS.themeDark;

  const isMenuOpen = Boolean(mobileAnchor);

  useEffect(() => {
    if (!isMenuOpen) return;
    let closedByPopState = false;
    window.history.pushState({ mobileMenu: true }, '', window.location.href);
    const handlePopState = () => {
      closedByPopState = true;
      setMobileAnchor(null);
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (!closedByPopState && window.history.state?.mobileMenu) window.history.back();
    };
  }, [isMenuOpen]);

  const closeMobileMenu = () => setMobileAnchor(null);
  const navTo = (path: string) => {
    // Replace the mobileMenu pushState so cleanup knows not to call history.back()
    window.history.replaceState(null, '', window.location.href);
    setMobileAnchor(null);
    navigate(path);
  };

  if (!user) return null;

  const projectSelect = (
    <Select
      variant="standard"
      value={currentProjectId ?? ''}
      onChange={(e) => onProjectChange(e.target.value)}
      displayEmpty
      inputProps={{ 'aria-label': PLANNING_LABELS.projectLabel }}
      sx={{
        '&:before': { borderBottom: 'none' },
        '&:after': { borderBottom: 'none' },
        '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
      }}
    >
      {activeProjects.length === 0 ? (
        <MenuItem disabled>{PLANNING_LABELS.noProjects}</MenuItem>
      ) : (
        activeProjects.map((p) => (
          <MenuItem key={p.id} value={p.id} data-testid={`option-${p.id}`}>
            {p.name} ({p.year})
          </MenuItem>
        ))
      )}
      {archivedProjects.length > 0 && [
        <Divider key="__archived_divider" />,
        <MenuItem
          key="__archived_label"
          disabled
          className="text-text-secondary uppercase text-xs opacity-100 pt-0.5 pb-1"
        >
          {PLANNING_LABELS.archivedSection}
        </MenuItem>,
        ...archivedProjects.map((p) => (
          <MenuItem key={p.id} value={p.id} data-testid={`option-${p.id}`}>
            {p.name} ({p.year})
          </MenuItem>
        )),
      ]}
    </Select>
  );

  if (isMobile) {
    return (
      <>
        <Tooltip title={LABELS.mobileMenuOpen}>
          <IconButton
            onClick={(e) => setMobileAnchor(e.currentTarget)}
            aria-label={LABELS.mobileMenuOpen}
            color="inherit"
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={mobileAnchor}
          open={Boolean(mobileAnchor)}
          onClose={closeMobileMenu}
          anchorReference="none"
          aria-label={LABELS.mobileMenuTitle}
          slotProps={{
            paper: {
              className:
                'w-screen max-w-[100vw] h-dvh max-h-dvh rounded-none !top-0 !left-0 flex flex-col',
            },
          }}
        >
          <div className="flex items-center justify-end px-4 mt-0 mb-2">
            <Tooltip title={LABELS.mobileMenuClose}>
              <IconButton
                onClick={closeMobileMenu}
                aria-label={LABELS.mobileMenuClose}
                color="inherit"
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </div>
          <Divider />
          <div className="flex-1 overflow-y-auto">
            <MenuItem onClick={() => navTo('/')}>
              <GridViewOutlinedIcon className="mr-3" fontSize="small" />
              {LABELS.planning}
            </MenuItem>
            <Divider />
            {projects.length > 0 && (
              <>
                <div className="px-4 py-4">{projectSelect}</div>
                <Divider />
              </>
            )}

            <MenuItem onClick={() => navTo('/users')}>
              <PeopleOutlinedIcon className="mr-3" fontSize="small" />
              {LABELS.users}
            </MenuItem>
            <MenuItem onClick={() => navTo('/projects')}>
              <FolderOutlinedIcon className="mr-3" fontSize="small" />
              {LABELS.projects}
            </MenuItem>
            <MenuItem onClick={() => navTo('/sites')}>
              <PlaceOutlinedIcon className="mr-3" fontSize="small" />
              {LABELS.sites}
            </MenuItem>
            <MenuItem onClick={() => navTo('/subcontractors')}>
              <EngineeringOutlinedIcon className="mr-3" fontSize="small" />
              {LABELS.subcontractors}
            </MenuItem>
            <MenuItem onClick={() => navTo('/suppliers')}>
              <LocalShippingOutlinedIcon className="mr-3" fontSize="small" />
              {LABELS.suppliers}
            </MenuItem>
            <MenuItem onClick={() => navTo('/machine-data')}>
              <CategoryOutlinedIcon className="mr-3" fontSize="small" />
              {LABELS.machineData}
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => navTo('/settings')}>
              <TuneIcon className="mr-3" fontSize="small" />
              {LABELS.general}
            </MenuItem>
            {isSuperAdmin && (
              <MenuItem onClick={() => navTo('/admin')}>
                <AdminPanelSettingsOutlinedIcon className="mr-3" fontSize="small" />
                {LABELS.adminTooltip}
              </MenuItem>
            )}

            <Divider />
            <MenuItem onClick={handleThemeToggle} data-testid="theme-toggle-menuitem">
              {isDark ? (
                <LightModeOutlinedIcon className="mr-3" />
              ) : (
                <DarkModeOutlinedIcon className="mr-3" />
              )}
              {themeTooltip}
            </MenuItem>
            <MenuItem onClick={() => navTo('/account')}>
              <AccountCircleOutlinedIcon className="mr-3" fontSize="small" />
              {LABELS.account}
            </MenuItem>
            <MenuItem
              component="a"
              href="/help"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                window.history.replaceState(null, '', window.location.href);
                setMobileAnchor(null);
              }}
            >
              <HelpOutlineOutlinedIcon className="mr-3" fontSize="small" />
              {LABELS.help}
            </MenuItem>
            <MenuItem onClick={() => { window.history.replaceState(null, '', window.location.href); signOut(); }} data-testid="sign-out-menuitem">
              <LogoutIcon className="mr-3" fontSize="small" />
              {LABELS.signOut}
            </MenuItem>
          </div>
        </Menu>
      </>
    );
  }

  return (
    <>
      {projects.length > 0 && (
        <>
          {projectSelect}
          <Divider orientation="vertical" flexItem className="mx-1 my-2" />
        </>
      )}
      {isSuperAdmin && (
        <Tooltip title={LABELS.adminTooltip}>
          <IconButton
            onClick={() => navigate('/admin')}
            aria-label={LABELS.adminTooltip}
            color="inherit"
          >
            <AdminPanelSettingsOutlinedIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={LABELS.menuButton}>
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={LABELS.menuButton}
          color="inherit"
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { className: 'min-w-48' } }}
      >
        <MenuItem onClick={() => { setAnchor(null); navigate('/users'); }}>
          <PeopleOutlinedIcon className="mr-3" fontSize="small" />{LABELS.users}
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); navigate('/projects'); }}>
          <FolderOutlinedIcon className="mr-3" fontSize="small" />{LABELS.projects}
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); navigate('/sites'); }}>
          <PlaceOutlinedIcon className="mr-3" fontSize="small" />{LABELS.sites}
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); navigate('/subcontractors'); }}>
          <EngineeringOutlinedIcon className="mr-3" fontSize="small" />{LABELS.subcontractors}
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); navigate('/suppliers'); }}>
          <LocalShippingOutlinedIcon className="mr-3" fontSize="small" />{LABELS.suppliers}
        </MenuItem>
        <MenuItem onClick={() => { setAnchor(null); navigate('/machine-data'); }}>
          <CategoryOutlinedIcon className="mr-3" fontSize="small" />{LABELS.machineData}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); navigate('/settings'); }}>
          <TuneIcon className="mr-3" fontSize="small" />{LABELS.general}
        </MenuItem>
      </Menu>
      <Tooltip title={LABELS.userMenuButton}>
        <Button
          onClick={(e) => setUserAnchor(e.currentTarget)}
          aria-label={LABELS.userMenuButton}
          variant="contained"
          className="text-xs w-10 h-10 min-w-10 rounded-full"
          data-testid="account-menu-button"
        >
          {initials}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={userAnchor}
        open={Boolean(userAnchor)}
        onClose={() => setUserAnchor(null)}
        slotProps={{ paper: { className: 'min-w-48' } }}
      >
        <MenuItem onClick={() => { setUserAnchor(null); navigate('/account'); }}>
          <AccountCircleOutlinedIcon className="mr-3" fontSize="small" />{LABELS.account}
        </MenuItem>
        <MenuItem
          component="a"
          href="/help"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setUserAnchor(null)}
        >
          <HelpOutlineOutlinedIcon className="mr-3" fontSize="small" />{LABELS.help}
        </MenuItem>
        <MenuItem onClick={() => { setUserAnchor(null); handleThemeToggle(); }} data-testid="theme-toggle-menuitem">
          {isDark ? <LightModeOutlinedIcon className="mr-3" fontSize="small" /> : <DarkModeOutlinedIcon className="mr-3" fontSize="small" />}
          {themeTooltip}
        </MenuItem>
        <MenuItem onClick={() => { setUserAnchor(null); signOut(); }} data-testid="sign-out-menuitem">
          <LogoutIcon className="mr-3" fontSize="small" />{LABELS.signOut}
        </MenuItem>
      </Menu>
    </>
  );
}
